import { ReactLocalization } from "@fluent/react";
import { z } from "zod";

// 17 characters, alphanumeric excluding I, O, and Q (disallowed by the VIN
// standard to avoid confusion with 1 and 0). Mirrors the backend validation.
export const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

export function createSchema(l10n: ReactLocalization, isRefinance = false, maxVehicleAgeYears = 0) {
  const currentYear = new Date().getFullYear();
  const maxValidVehicleYear = currentYear + 1;
  // When maxVehicleAgeYears is 0, accept any year (down to 1886). Otherwise,
  // restrict the oldest acceptable year to maxVehicleAgeYears back from now.
  const minValidVehicleYear = maxVehicleAgeYears > 0 ? currentYear - maxVehicleAgeYears : 1886;

  const yearTooOldMessage =
    maxVehicleAgeYears > 0
      ? l10n.getString(
          "auto-loan-auto-details-year-too-old",
          { minYear: String(minValidVehicleYear) },
          `Year cannot be older than ${minValidVehicleYear}`
        )
      : l10n.getString("auto-loan-auto-details-year-invalid", null, "Enter a valid year");

  return z
    .object({
      condition: z.enum(["new", "used"]),
      year: z
        .string()
        .min(1, { message: l10n.getString("error-required", null, "Required") })
        .refine((value) => /^\d{4}$/.test(value), {
          message: l10n.getString(
            "auto-loan-auto-details-year-invalid",
            null,
            "Enter a valid year"
          ),
        })
        .transform(Number)
        .refine((value) => value <= maxValidVehicleYear, {
          message: l10n.getString(
            "auto-loan-auto-details-year-too-new",
            { maxYear: String(maxValidVehicleYear) },
            `Year cannot be later than ${maxValidVehicleYear}`
          ),
        })
        .refine((value) => value >= minValidVehicleYear, {
          message: yearTooOldMessage,
        }),
      vin: z
        .string()
        .optional()
        .refine((value) => !value || VIN_PATTERN.test(value), {
          message: l10n.getString(
            "auto-loan-auto-details-vin-invalid",
            null,
            "Enter a valid 17-character VIN"
          ),
        }),
      make: z.string().min(1, { message: l10n.getString("error-required", null, "Required") }),
      model: z.string().min(1, { message: l10n.getString("error-required", null, "Required") }),
      trim: z.string().optional(),
      mileage: z.coerce
        .number({
          invalid_type_error: l10n.getString(
            "auto-loan-auto-details-mileage-invalid",
            null,
            "Enter a valid mileage"
          ),
        })
        .min(0, {
          message: l10n.getString(
            "auto-loan-auto-details-mileage-invalid",
            null,
            "Enter a valid mileage"
          ),
        })
        .optional(),
    })
    .refine(
      (data) => {
        const resolvedCondition = isRefinance ? "used" : data.condition;

        // Require mileage only if condition is "used"
        if (resolvedCondition === "used" && (data.mileage === undefined || data.mileage === null)) {
          return false;
        }
        return true;
      },
      {
        message: l10n.getString("error-required", null, "Required"),
        path: ["mileage"],
      }
    );
}

// Output type (after zod transforms, e.g. year: number)
export type AutoDetailsFormData = z.infer<ReturnType<typeof createSchema>>;

// Input type (before zod transforms, e.g. year: string as entered in the text input)
export type AutoDetailsFormInput = z.input<ReturnType<typeof createSchema>>;
